import { Button } from '@kcftech/design-system';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { useHighlightedIndex } from 'custom_hooks/useHighlightedIndex';
import { useKeyPress } from 'custom_hooks/useKeyPress';
import { useKeyPressed } from 'custom_hooks/useKeyPressed';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const PopupContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
`;

const Label = styled.label`
    display: inline-flex;
    gap: 0.8em;
    cursor: pointer;
    align-items: center;

    padding-left: 0.2rem;
    padding-right: 0.2rem;
    border-radius: 0.6rem;
    border: 1px solid transparent;
    &:hover,
    &:focus-visible,
    &.highlighted {
        border-color: ${tokens.action.hover};
    }
    &:active,
    &.active {
        background-color: ${tokens.action.selected};
    }
`;

const RadioButton = styled.input`
    cursor: pointer;
    width: 1.4rem;
    height: 1.4rem;
    flex-shrink: 0;
`;

const LabelContentWrapper = styled.span`
    display: inline-flex;
    justify-content: space-between;
    gap: 4rem;
    flex-grow: 1;

    --p: 0.28rem;
    padding-top: var(--p);
    padding-bottom: var(--p);
`;

export type MultiSelectQuickFilterOption<TValue> = {
    value: TValue;
    /**
     * Label can be a string or ReactNode (e.g., a Lozenge component)
     */
    label: ReactNode;
    /**
     * Optional extra content to display on the right side of the label
     * (e.g., counts, badges, etc.)
     */
    extra?: ReactNode;
};

export type RadioSelectQuickFilterPopupProps<TValue> = {
    close: () => void;
    maxHeight: string;
    selectedValue: TValue | undefined;
    onChangeValue: (value: TValue) => void;
    popupRef: React.RefObject<HTMLDivElement>;
    triggerRef: React.RefObject<HTMLButtonElement>;
    /**
     * All available options to display in the popup.
     * Should already be filtered and sorted as desired.
     */
    options: MultiSelectQuickFilterOption<TValue>[];
};

export function RadioSelectQuickFilterPopup<TValue>({
    close,
    maxHeight,
    selectedValue,
    onChangeValue,
    popupRef,
    triggerRef,
    options,
}: RadioSelectQuickFilterPopupProps<TValue>) {
    useKeyPress('Escape', () => {
        close();
    });

    const isSelected = (value: TValue) => selectedValue === value;

    const onChange = (value: TValue) => {
        onChangeValue(value);
    };

    const [highlightedIndex, setHighlightedIndex] = useState<number>();

    const keyHandler = useHighlightedIndex({
        items: options,
        highlightedIndex: highlightedIndex ?? -1,
        onChange: (idx) => {
            setHighlightedIndex(idx);

            const el = document.getElementById(`opt-${idx}`);
            if (!el) {
                return;
            }

            el.scrollIntoView({
                behavior: 'instant',
                block: 'nearest',
                inline: 'nearest',
            });
            el.focus();
        },
        onSelect: (item) => {
            onChange(item.value);
        },
        spaceSubmits: true,
    });

    useKeyPress(['ArrowUp', 'ArrowDown', 'Enter'], keyHandler);
    const isEnterPressed = useKeyPressed('Enter');

    return (
        <StatelessPopup
            ref={popupRef}
            onClose={close}
            anchor={triggerRef.current}
            collision={{
                horizontal: 'fit',
                vertical: 'none',
            }}
            popupSettings={{}}
            noOverlay
            noPadding
        >
            <div
                style={{
                    maxHeight,
                    padding: '1rem',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Button
                    className="mb-5"
                    appearance="secondary"
                    spacing="compact"
                    disabled={selectedValue === undefined}
                    onClick={() => {
                        // Clear selection - select first option as default
                        if (options.length > 0) {
                            onChangeValue(options[0].value);
                        }
                    }}
                >
                    Reset
                </Button>
                <PopupContentWrapper>
                    {options.map((option, idx) => (
                        <Label
                            key={idx}
                            id={`opt-${idx}`}
                            className={
                                highlightedIndex === idx
                                    ? 'highlighted' + (isEnterPressed ? ' active' : '')
                                    : ''
                            }
                            onMouseEnter={() => {
                                setHighlightedIndex(idx);
                            }}
                            onClick={() => onChange(option.value)}
                        >
                            <RadioButton
                                type="radio"
                                tabIndex={-1}
                                checked={isSelected(option.value)}
                                aria-selected={
                                    highlightedIndex === idx ? 'true' : undefined
                                }
                                onChange={() => onChange(option.value)}
                                onBlur={(e) => {
                                    if (
                                        e.relatedTarget &&
                                        e.relatedTarget !== triggerRef.current &&
                                        !popupRef.current?.contains(e.relatedTarget)
                                    ) {
                                        close();
                                    }
                                }}
                            />
                            <LabelContentWrapper>
                                <span>{option.label}</span>
                                {option.extra && <span>{option.extra}</span>}
                            </LabelContentWrapper>
                        </Label>
                    ))}
                </PopupContentWrapper>
            </div>
        </StatelessPopup>
    );
}
